/**
 * Referral Context
 * Manages referral state, access control, and code application
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWallet } from './WalletProvider';
import * as referralApi from '../services/referralApiService';

interface ReferralContextType {
  // Access control
  hasAccess: boolean | null; // null = checking, true/false = determined
  needsReferral: boolean;
  accessReason: string | null;
  
  // Pending referral code (from URL)
  pendingCode: string | null;
  
  // User's referral code (for sharing)
  myReferralCode: string | null;
  
  // Stats
  stats: referralApi.ReferralStats | null;
  
  // Earnings
  earnings: referralApi.ReferralEarnings | null;
  
  // Loading states
  loading: {
    access: boolean;
    applyingCode: boolean;
    generatingCode: boolean;
    stats: boolean;
    earnings: boolean;
    claiming: boolean;
  };
  
  // Actions
  checkAccess: () => Promise<void>;
  applyReferralCode: (code: string) => Promise<{ success: boolean; error?: string }>;
  getMyCode: () => Promise<void>;
  refreshStats: () => Promise<void>;
  refreshEarnings: () => Promise<void>;
  claimEarnings: () => Promise<{ success: boolean; data?: any; error?: string }>;
}

const ReferralContext = createContext<ReferralContextType | undefined>(undefined);

export function ReferralProvider({ children }: { children: React.ReactNode }) {
  const { address: wallet } = useWallet();
  const location = useLocation();
  const navigate = useNavigate();
  
  // State
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [needsReferral, setNeedsReferral] = useState(false);
  const [accessReason, setAccessReason] = useState<string | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const [myReferralCode, setMyReferralCode] = useState<string | null>(null);
  const [stats, setStats] = useState<referralApi.ReferralStats | null>(null);
  const [earnings, setEarnings] = useState<referralApi.ReferralEarnings | null>(null);
  
  const [loading, setLoading] = useState({
    access: false,
    applyingCode: false,
    generatingCode: false,
    stats: false,
    earnings: false,
    claiming: false
  });
  
  /**
   * Extract referral code from URL on mount
   */
  useEffect(() => {
    const pathname = location.pathname;
    const code = pathname.slice(1); // Remove leading "/"
    
    // Ignore known routes
    if (code === 'admin' || code === '' || !code) {
      // Check session storage for code
      const storedCode = sessionStorage.getItem('pendingReferralCode');
      if (storedCode) {
        setPendingCode(storedCode);
      }
      return;
    }
    
    // This looks like a referral code
    setPendingCode(code);
    sessionStorage.setItem('pendingReferralCode', code);
  }, [location.pathname]);
  
  /**
   * Listen for manual code entry (from modal)
   */
  useEffect(() => {
    const handleStorageChange = () => {
      const storedCode = sessionStorage.getItem('pendingReferralCode');
      if (storedCode && storedCode !== pendingCode) {
        setPendingCode(storedCode);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [pendingCode]);
  
  /**
   * Check access when wallet connects
   */
  const checkAccess = useCallback(async () => {
    if (!wallet) {
      setHasAccess(null);
      return;
    }
    
    setLoading(prev => ({ ...prev, access: true }));
    
    try {
      const result = await referralApi.checkAccess(wallet);
      
      if (result.success && result.data) {
        setHasAccess(result.data.canAccess);
        setNeedsReferral(result.data.needsReferral);
        setAccessReason(result.data.reason);
      } else {
        console.error('❌ REFERRAL: Access check failed:', result.error);
        // On error, assume they need referral (safe default)
        setHasAccess(false);
        setNeedsReferral(true);
      }
    } catch (error) {
      console.error('❌ REFERRAL: Error checking access:', error);
      setHasAccess(false);
      setNeedsReferral(true);
    } finally {
      setLoading(prev => ({ ...prev, access: false }));
    }
  }, [wallet]);
  
  /**
   * Apply referral code
   */
  const applyReferralCode = useCallback(async (code: string): Promise<{ success: boolean; error?: string }> => {
    if (!wallet) {
      return { success: false, error: 'Wallet not connected' };
    }
    
    setLoading(prev => ({ ...prev, applyingCode: true }));
    
    try {
      const result = await referralApi.applyCode(wallet, code);
      
      if (result.success) {
        // Clear pending code
        setPendingCode(null);
        sessionStorage.removeItem('pendingReferralCode');
        
        // Recheck access
        await checkAccess();
        
        return { success: true };
      } else {
        console.error('❌ REFERRAL: Failed to apply code:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('❌ REFERRAL: Error applying code:', error);
      return { success: false, error: 'Failed to apply code' };
    } finally {
      setLoading(prev => ({ ...prev, applyingCode: false }));
    }
  }, [wallet, checkAccess]);
  
  /**
   * Get user's referral code (lazy generation)
   */
  const getMyCode = useCallback(async () => {
    if (!wallet) return;
    
    setLoading(prev => ({ ...prev, generatingCode: true }));
    
    try {
      const result = await referralApi.getMyCode(wallet);
      
      if (result.success && result.data) {
        setMyReferralCode(result.data.code);
      }
    } catch (error) {
      console.error('❌ REFERRAL: Error getting code:', error);
    } finally {
      setLoading(prev => ({ ...prev, generatingCode: false }));
    }
  }, [wallet]);
  
  /**
   * Refresh referral stats
   */
  const refreshStats = useCallback(async () => {
    if (!wallet) return;
    
    setLoading(prev => ({ ...prev, stats: true }));
    
    try {
      const result = await referralApi.getStats(wallet);
      
      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('❌ REFERRAL: Error refreshing stats:', error);
    } finally {
      setLoading(prev => ({ ...prev, stats: false }));
    }
  }, [wallet]);
  
  /**
   * Refresh earnings info
   */
  const refreshEarnings = useCallback(async () => {
    if (!wallet) return;
    
    setLoading(prev => ({ ...prev, earnings: true }));
    
    try {
      const result = await referralApi.getEarnings(wallet);
      
      if (result.success && result.data) {
        setEarnings(result.data.earnings);
      }
    } catch (error) {
      console.error('❌ REFERRAL: Error refreshing earnings:', error);
    } finally {
      setLoading(prev => ({ ...prev, earnings: false }));
    }
  }, [wallet]);
  
  /**
   * Claim referral earnings
   */
  const claimEarnings = useCallback(async () => {
    if (!wallet) {
      return { success: false, error: 'No wallet connected' };
    }
    
    setLoading(prev => ({ ...prev, claiming: true }));
    
    try {
      const result = await referralApi.claimEarnings(wallet);
      
      if (result.success) {
        // Refresh earnings after successful claim
        await refreshEarnings();
      }
      
      return result;
    } catch (error) {
      console.error('❌ REFERRAL: Error claiming earnings:', error);
      return { success: false, error: 'Failed to claim earnings' };
    } finally {
      setLoading(prev => ({ ...prev, claiming: false }));
    }
  }, [wallet, refreshEarnings]);
  
  /**
   * Auto-apply pending code when wallet connects
   * OR clear code if user already has access
   */
  useEffect(() => {
    if (wallet && pendingCode) {
      // If user already has access, just clear the code and navigate
      if (hasAccess === true) {
        setPendingCode(null);
        sessionStorage.removeItem('pendingReferralCode');
        navigate('/', { replace: true });
        return;
      }
      
      // Otherwise, try to apply the code
      applyReferralCode(pendingCode).then((result) => {
        if (result.success) {
          // Navigate to home after successful application
          navigate('/', { replace: true });
        } else {
          // If apply failed (e.g., already used code), still navigate home
          setPendingCode(null);
          sessionStorage.removeItem('pendingReferralCode');
          navigate('/', { replace: true });
        }
      });
    }
  }, [wallet, pendingCode, hasAccess, applyReferralCode, navigate]);
  
  /**
   * Check access when wallet changes
   */
  useEffect(() => {
    if (wallet) {
      checkAccess();
    }
  }, [wallet, checkAccess]);
  
  const value: ReferralContextType = {
    hasAccess,
    needsReferral,
    accessReason,
    pendingCode,
    myReferralCode,
    stats,
    earnings,
    loading,
    checkAccess,
    applyReferralCode,
    getMyCode,
    refreshStats,
    refreshEarnings,
    claimEarnings
  };
  
  return (
    <ReferralContext.Provider value={value}>
      {children}
    </ReferralContext.Provider>
  );
}

export function useReferral() {
  const context = useContext(ReferralContext);
  if (context === undefined) {
    throw new Error('useReferral must be used within ReferralProvider');
  }
  return context;
}

export default ReferralContext;

